import React, { useEffect, useRef } from 'react';
import { usePlayer } from '../contexts/PlayerContext';
import { ParticleField } from './ParticleField';

/**
 * Room light for the app. Layered, not decorative filler:
 * two pools of accent light whose brightness tracks the live audio level, a
 * slow rotating groove field, a periodic light sweep, film grain, and a
 * vignette that keeps the reading column the brightest thing on screen.
 */
export function AmbientBackdrop() {
  const goldRef = useRef<HTMLDivElement | null>(null);
  const irisRef = useRef<HTMLDivElement | null>(null);
  const grooveRef = useRef<HTMLDivElement | null>(null);
  const {
    getLevel,
    isPlaying
  } = usePlayer();
  const levelFn = useRef(getLevel);
  const playing = useRef(isPlaying);
  levelFn.current = getLevel;
  playing.current = isPlaying;
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    let smoothed = 0;
    const start = performance.now();
    const tick = () => {
      frame = requestAnimationFrame(tick);
      const raw = playing.current ? levelFn.current() : 0;
      smoothed += (raw - smoothed) * 0.06;
      const t = (performance.now() - start) / 1000;
      if (irisRef.current) {
        irisRef.current.style.opacity = String(0.22 + smoothed * 0.5);
        irisRef.current.style.transform = `translate3d(${Math.cos(t / 8) * 5}%, ${Math.sin(t / 12) * 4}%, 0) scale(${1 + smoothed * 0.24})`;
      }
      if (goldRef.current) {
        goldRef.current.style.opacity = String(0.16 + smoothed * 0.44);
        goldRef.current.style.transform = `translate3d(${Math.sin(t / 9) * 4}%, ${Math.cos(t / 11) * 3}%, 0) scale(${1 + smoothed * 0.2})`;
      }
      if (grooveRef.current) {
        grooveRef.current.style.opacity = String(0.06 + smoothed * 0.16);
      }
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, []);
  return <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden" data-id="element-42" data-mp="keo1rzssaxmt">
      {/* Accent light pools, driven by the analyser */}
      <div ref={irisRef} className="absolute -left-40 -top-52 h-[40rem] w-[40rem] rounded-full blur-[130px]" style={{
      background: 'radial-gradient(circle, rgb(var(--secondary)) 0%, rgb(var(--secondary) / 0) 70%)'
    }} data-id="element-43" data-mp="ccne7rixn00c" />
      <div ref={goldRef} className="absolute -bottom-60 right-[-12rem] h-[36rem] w-[36rem] rounded-full blur-[140px]" style={{
      background: 'radial-gradient(circle, rgb(var(--accent)) 0%, rgb(var(--accent) / 0) 70%)'
    }} data-id="element-44" data-mp="h6rnihfo7k4l" />

      {/* Slowly turning record groove field */}
      <div ref={grooveRef} className="groove-layer animate-orbit absolute left-1/2 top-1/2 h-[120rem] w-[120rem] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.06]" data-id="element-45" data-mp="3fczfw6wosqo" />

      {/* Periodic light sweep, like a passing car */}
      <div className="absolute inset-y-0 left-0 w-[38%] animate-sweep opacity-0 blur-3xl" style={{
      background: 'linear-gradient(100deg, rgb(var(--accent) / 0) 0%, rgb(var(--accent-soft) / 0.16) 50%, rgb(var(--accent) / 0) 100%)'
    }} data-id="element-46" data-mp="19udywbv53t20" />

      {/* Drifting dust, lifted by the audio level */}
      <ParticleField data-id="element-47" data-mp="2isdu24s57tck" />

      {/* Vignette + grain */}
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(120% 90% at 50% 0%, rgb(var(--surface-base) / 0) 30%, rgb(var(--surface-base) / 0.85) 100%)'
    }} data-id="element-48" data-mp="2itoy85pi3347" />
      <div className="grain-layer absolute inset-0 opacity-[0.045] mix-blend-soft-light" data-id="element-49" data-mp="3fi4mzzksd8g9" />
    </div>;
}